import React from 'react';
import { Call } from '@/types';

interface CallCardProps {
  call: Call;
  onStatusChange?: (callId: string, newStatus: Call['status']) => void;
  onCardClick?: (callId: string) => void;
}

// Get the approval status text for URGENTLY calls
const getUrgentlyApprovalStatus = (status: string) => {
  switch (status) {
    case 'COMPLETED_AWAITING':
      return 'Awaiting Approval';
    case 'GOA_AWAITING':
      return 'GOA Awaiting Approval';
    case 'COMPLETED':
      return 'Completion Approved';
    case 'GOA':
      return 'GOA Approved';
    default:
      return null;
  }
};

const CallCard: React.FC<CallCardProps> = ({ call, onStatusChange, onCardClick }) => {
  const getStatusButton = (status: Call['status']) => {
    // Convert backend status to frontend status (similar to CallScreen)
    const statusMap: { [key: string]: string } = {
      'NEW': 'ready',
      'PENDING': 'waiting', 
      'DISPATCHED': 'en_route',
      'ON_SCENE': 'on_scene',
      'TOWING': 'towing',
      'COMPLETED': 'complete',
      'COMPLETED_AWAITING': 'complete',
      'GOA': 'complete',
      'GOA_AWAITING': 'complete',
      'FAILED': 'complete',
      'CANCELED': 'canceled',
      'CANCELLED': 'canceled',
    };
    
    // Use mapped status or original status if already frontend format
    const mappedStatus = statusMap[status] || status;
    
    switch (mappedStatus) {
      case 'ready':
        return {
          text: 'Ready',
          className: 'bg-green-100 text-green-700 border border-green-200',
        };
      case 'waiting':
        return {
          text: 'Waiting',
          className: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
        };
      case 'en_route':
        return {
          text: 'En Route',
          className: 'bg-blue-100 text-blue-700 border border-blue-200',
        };
      case 'on_scene':
        return {
          text: 'On Scene',
          className: 'bg-orange-100 text-orange-700 border border-orange-200',
        };
      case 'towing':
        return {
          text: 'Towing',
          className: 'bg-teal-100 text-teal-700 border border-teal-200',
        };
      case 'complete':
        return {
          text: 'Complete',
          className: 'bg-purple-100 text-purple-700 border border-purple-200',
        };
      case 'canceled':
        return {
          text: 'Canceled',
          className: 'bg-gray-100 text-gray-700 border border-gray-200',
        };
      default:
        return {
          text: 'Unknown',
          className: 'bg-gray-100 text-gray-700 border border-gray-200',
        };
    }
  };

  // Format dispatch time to be human readable
  const formatDispatchTime = (timeString: string) => {
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      return timeString; // Fallback to original if parsing fails
    }
  };

  // Format account info to be human readable
  const formatAccountInfo = (accountInfo: string) => {
    return accountInfo.replace(/_/g, ' ');
  };

  const statusButton = getStatusButton(call.status);

  const handleCardClick = () => {
    onCardClick?.(call.id);
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStatusChange?.(call.id, call.status);
  };

  return (
    <div 
      className="bg-background border border-gray-200 rounded-lg p-4 shadow-sm flex justify-between items-center cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleCardClick}
    >
      <div className="flex-1 mr-3">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-black">{call.address}</h3>
          {call.source === 'URGENTLY' && getUrgentlyApprovalStatus(call.status) && (
            <span className="text-xs font-medium text-gray-600">
              {getUrgentlyApprovalStatus(call.status)}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-1">Dispatched at {formatDispatchTime(call.dispatchedTime)}</p>
   
      </div>
      <button 
        className={`px-4 py-2 rounded-full text-sm font-semibold min-w-[80px] transition-all hover:opacity-80 ${statusButton.className}`}
        onClick={handleStatusClick}
      >
        {statusButton.text}
      </button>
    </div>
  );
};

export default CallCard; 